import { computed, inject } from '@angular/core'
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals'
import { ToastrService } from 'ngx-toastr'
import { Observable, tap } from 'rxjs'
import { AppointmentService } from 'src/app/core/services/appointment/appointment.service'
import { ApplianceService } from 'src/app/core/services/appliance.service'
import { CustomerService } from 'src/app/core/services/customer.service'
import { SmartQuoteService } from 'src/app/core/services/smartquote/smartquote.service'
import { ConfirmationDialogService } from 'src/app/shared/components/confirmation-dialog/confirmation-dialog.service'
import { Appointment } from 'src/app/shared/interfaces/appointment/appointment.interface'
import { CompanySetting } from 'src/app/shared/interfaces/company-setting/company-setting.interface'
import { Appliance } from 'src/app/shared/interfaces/appliances/appliance.interface'
import { Customer } from 'src/app/shared/interfaces/customer/customer.interface'
import { SmartQuote } from 'src/app/shared/interfaces/smartquote/smart-quote.interface'
import { environment } from 'src/environments/environment'

interface SmartQuoteState {
    quote: SmartQuote | null
    customer: Customer | null
    companySettings: CompanySetting | null
    appointment: Appointment | null
    mainAppliance: Appliance | null
}

const initialState: SmartQuoteState = {
    quote: null,
    customer: null,
    companySettings: null,
    appointment: null,
    mainAppliance: null,
}

export const SmartQuoteStore = signalStore(
    { providedIn: 'root', protectedState: false },
    withState<SmartQuoteState>(initialState),
    withComputed((store) => ({
        isNewQuote: computed(() => {
            return store.quote() === null
        }),
        isAccepted: computed(() => {
            return store.quote()?.accepted || false
        }),
        isDeclined: computed(() => {
            return store.quote()?.declined || false
        }),
        hasOrderBeenPlaced: computed(() => {
            return store.quote()?.hasOrderBeenPlaced || false
        }),
        partMarkup: computed(() => {
            return store.companySettings()?.smartQuotePartsMarkup
                ? store.companySettings()!.smartQuotePartsMarkup / 100 + 1
                : 1
        }),
        landingPageUrl: computed(() => {
            const smartQuoteId = store.quote()?.id || 0
            const customerApiKey = store.customer()?.apiKey || ''
            const companyId = store.companySettings()?.companyId || 0
            return `${environment.legacyUrl}public/smartquoteaccept/${companyId}/${smartQuoteId}/${customerApiKey}`
        }),
    })),
    withMethods((store) => {
        const appointmentService = inject(AppointmentService)
        const smartQuoteService = inject(SmartQuoteService)
        const confirmationService = inject(ConfirmationDialogService)
        const customerService = inject(CustomerService)
        const customerApplianceService = inject(ApplianceService)
        const toastr = inject(ToastrService)

        return {
            setSmartQuote(smartqote: SmartQuote | null): void {
                patchState(store, { quote: smartqote })
            },
            setCustomer(customer: Customer, appointment: Appointment | null, mainAppliance: Appliance | null): void {
                patchState(store, { customer: customer })
                patchState(store, { appointment: appointment })
                patchState(store, { mainAppliance: mainAppliance })
            },
            setCompanySettings(companySettings: CompanySetting): void {
                patchState(store, { companySettings: companySettings })
            },
            saveQuote(quoteToUpdate: SmartQuote): Observable<SmartQuote | null> {
                if (store.isNewQuote()) {
                    return smartQuoteService.createSmartquote(quoteToUpdate).pipe(
                        tap((quote) => {
                            toastr.success('SmartQuote Created', 'Success')
                            patchState(store, { quote: quote })
                        })
                    )
                } else {
                    return smartQuoteService.updateSmartquote(store.quote()!.id, quoteToUpdate).pipe(
                        tap(() => {
                            toastr.success('SmartQuote Updated', 'Success')
                            patchState(store, { quote: { ...store.quote()!, id: store.quote()!.id } })
                        })
                    )
                }
            },
            acceptQuote(): void {
                if (store.isAccepted()) {
                    toastr.error('This quote has already been accepted')
                    return
                }

                confirmationService
                    .confirm(
                        'Are you sure?',
                        'Are you sure you want to accept this quote?',
                        'Yes Accept Quote',
                        'Cancel'
                    )
                    .then((confirmed) => {
                        if (!confirmed) {
                            return
                        }

                        smartQuoteService.acceptSmartquote(store.quote()!.id).subscribe((_) => {
                            toastr.success('SmartQuote Accepted', 'Success')
                            patchState(store, { quote: { ...store.quote()!, accepted: true, declined: false } })
                        })
                    })
            },
            declineQuote(): void {
                if (!store.quote()) {
                    return
                }

                if (store.isDeclined()) {
                    toastr.error('This quote has already been declined')
                    return
                }

                confirmationService
                    .confirm(
                        'Are you sure?',
                        'Are you sure you want to decline this quote?',
                        'Yes Decline Quote',
                        'Cancel'
                    )
                    .then((confirmed) => {
                        if (!confirmed) {
                            return
                        }

                        smartQuoteService.declineSmartquote(store.quote()!.id).subscribe((_) => {
                            toastr.info('SmartQuote Declined', 'Confirmed')
                            patchState(store, { quote: { ...store.quote()!, declined: true, accepted: false } })
                        })
                    })
            },
        }
    })
)
